/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import { TParams } from '@global-types/app/app';
import {
	formatNumberWithSpaces,
	formatTokenAvailable,
} from '@utils/formats';
import { randomId } from '@mantine/hooks';
import isNil from 'lodash/isNil';
import { NOT_APPLICABLE } from '@config/constants/common';
import { getUnitLabel } from '@utils/units';
import isNumber from 'lodash/isNumber';
import {
	TAdditionalityProject,
	TMeasurabilityProject,
} from '@global-types/api/api';
import { convertGCO2EToTCO2EWithoutUnit } from '@utils/functions';

export type TParsedRowMeasurabilityProject = {
	id: string;
	name: string;
	price: number;
	availableStock: string;
};

export type TParsedRowAdditionalityProject = {
	id: string;
	name: string;
	target_euro: number;
	coverage: number;
};

export const getProjects = (
	params: any
): Promise<{
	rows: any[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}> => {
	return apiClient.get('/projects', {
		params: {
			query: params,
			sort: 'name',
			pageSize: 1000,
		},
	});
};

export const useProjects = (
	enabled: { enabled: boolean },
	queryParams?: TParams
) => {
	const { data, isFetching, isFetched } = useQuery({
		queryKey: ['projects', { queryParams: queryParams }],
		queryFn: () => getProjects(queryParams),
		staleTime: 0,
		enabled: enabled.enabled,
	});

	const updateMeasurabilities = (
		mesurabilities: TMeasurabilityProject[]
	) => {
		return mesurabilities.reduce(
			(
				acc: TParsedRowMeasurabilityProject[],
				currentItem: TMeasurabilityProject
			) => {
				const modifiedItem = {
					id: currentItem.id,
					name: !isNil(currentItem.name)
						? currentItem.name
						: NOT_APPLICABLE,

					price: currentItem.sellPrice_europertco2eq,
					availableStock: `${
						currentItem?.quantity?.remaining_aco2
							? formatNumberWithSpaces(
									Number(
										formatTokenAvailable(
											convertGCO2EToTCO2EWithoutUnit(
												currentItem.quantity
													.remaining_aco2
											)
										)
									)
								)
							: 0
					} ${getUnitLabel('tco2eq')}`,
				};

				acc.push(modifiedItem);

				return acc;
			},
			[]
		);
	};

	const updateAdditionalities = (
		additionalities: TAdditionalityProject[]
	) => {
		return additionalities.reduce(
			(
				acc: TParsedRowAdditionalityProject[],
				currentItem: TAdditionalityProject
			) => {
				const coverage =
					currentItem.financing &&
					isNumber(currentItem.financing.pledged_euro)
						? currentItem.financing.pledged_euro
						: 0;
				const modifiedItem = {
					id: randomId(),
					name: currentItem.name,
					target_euro:
						!isNil(currentItem.financing) &&
						!isNil(currentItem.financing.target_euro)
							? currentItem.financing.target_euro
							: 0,
					coverage,
				};

				acc.push(modifiedItem);

				return acc;
			},
			[]
		);
	};

	return {
		additionalities:
			data?.rows && Array.isArray(data?.rows)
				? updateAdditionalities(
						data?.rows.filter(
							(project) => project.__t == 'Additionality'
						)
					)
				: [],
		measurabilities:
			data?.rows && Array.isArray(data?.rows)
				? updateMeasurabilities(
						data?.rows.filter(
							(project) => project.__t == 'Measurability'
						)
					)
				: [],
		isLoading: isFetching && !isFetched,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
